import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as campaignApi from '../api/campaigns';
import type { CreateCampaignRequest } from '@charbuilder/shared';
import { queryKeys } from '../lib/query-keys';

export function useCampaigns() {
  return useQuery({
    queryKey: queryKeys.campaigns.all,
    queryFn: campaignApi.getCampaigns,
  });
}

export function useCampaign(id: string) {
  return useQuery({
    queryKey: queryKeys.campaigns.detail(id),
    queryFn: () => campaignApi.getCampaign(id),
    enabled: !!id,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCampaignRequest) => campaignApi.createCampaign(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.campaigns.all }),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => campaignApi.deleteCampaign(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.campaigns.all }),
  });
}

export function useInvitePlayer(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (username: string) => campaignApi.invitePlayer(campaignId, username),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.campaigns.detail(campaignId) }),
  });
}

export function useRespondToInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, status }: { campaignId: string; status: 'ACCEPTED' | 'DECLINED' }) =>
      campaignApi.respondToInvite(campaignId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.campaigns.all });
      qc.invalidateQueries({ queryKey: queryKeys.invites.all });
    },
  });
}

export function usePendingInvites() {
  return useQuery({
    queryKey: queryKeys.invites.all,
    queryFn: campaignApi.getPendingInvites,
  });
}
